import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MessageService } from "../../modules/shared/message/message.service";
import { ProgressBarService } from "../../modules/shared/progress-bar/progress-bar.service";
import { LocalStorageKey } from "../../local-storage/local-storage-keys";
import { BaseHttpService } from "../base-http.service";
import { HttpClient } from "@angular/common/http";
import { LocalStorageService } from "../../local-storage/local-storage.service";
import { ChangePassword } from "../../model/changepassword";
import { bootIntercom, shutdownIntercom } from "../../util/intercom.util";
import { TranslateService } from "@ngx-translate/core";
import { User } from "../../model";
import { environment } from "../../../environments/environment";
import { CombiPackageLoadUserResult } from "../combipackage/combipackage.service";
import { firstValueFrom } from "rxjs";
import { syncSessionCookie } from "../../util/session-cookie.util";

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseHttpService {

  private user: User;
  private adminUser: User;
  public languageOverride: string;

  constructor(protected http: HttpClient,
              protected progressBarService: ProgressBarService,
              protected messageService: MessageService,
              private translate: TranslateService,
              private localStorageService: LocalStorageService,
              private router: Router) {
    super(http, null, translate, progressBarService, messageService);
  }

  protected override getCurrentUser(): User {
    return this.getUser();
  }

  isLoggedIn(): boolean {
    if (this.user && !this.user.temporaryUser) {
      return true;
    }

    const user = this.localStorageService.get<User>(LocalStorageKey.USER);

    if (user === null) {
      return false;
    } else {
      this.user = user;
      return user && user.loggedIn && !user.temporaryUser;
    }
  }

  getUser(): User {
    if (!this.user) {
      this.user = this.localStorageService.get<User>(LocalStorageKey.USER);
    }
    return this.user;
  }

  setUser(user: User, alsoBootIntercom = true): void {
    this.user = user;
    this.localStorageService.set(LocalStorageKey.USER, user);
    syncSessionCookie(!!user);
    if (this.languageOverride) {
      this.translate.use(this.languageOverride);
      if (this.user) {
        this.user.language = this.languageOverride;
      }
    } else if (this.user?.language) {
      this.translate.use(this.user.language);
    }

    if (alsoBootIntercom) {
      bootIntercom(this, this.translate.getCurrentLang(), user);
    }
  }

  isSuperUser(): boolean {
    return this.getUser().cindyLoper || !!this.getAdminUser();
  }

  getAdminUser(): User {
    if (this.adminUser === undefined) {
      this.adminUser = this.localStorageService.get<User>(LocalStorageKey.ADMIN_USER);
    }
    return this.adminUser;
  }

  setAdminUser(adminUser: User): void {
    this.adminUser = adminUser;
    this.localStorageService.set(LocalStorageKey.ADMIN_USER, adminUser);
  }

  async login(email: string, password: string): Promise<boolean> {
    const user = this.getUser() || <User>{};
    this.clearLocalStorage();
    this.localStorageService.remove(LocalStorageKey.ADMIN_USER);
    user.email = email;
    user.password = password;
    await this.completeUserBeforeSaving(user);
    this.setUser((await this.post<User>("unauthenticated/user/login", {user})).result, true);
    return this.isLoggedIn();
  }

  async autoLogin(token: string): Promise<boolean> {
    const loginAsUser = (await this.get<User>(`unauthenticated/user/autologin/${token}`)).result;
    if (this.getUser()?.id && this.getUser().id !== loginAsUser?.id) {
      this.messageService.showErrorMessage("Log eerst even uit - check al je browser tabs!");
      return;
    }
    this.clearLocalStorage();
    this.setUser(loginAsUser);
    return this.isLoggedIn();
  }

  async autoLoginAdmin(token: string): Promise<boolean> {
    const loginAsUser = (await this.get<User>(`unauthenticated/user/autologinadmin/${token}`)).result;
    if (this.getUser()?.id && this.getUser().id !== loginAsUser?.id) {
      this.messageService.showErrorMessage("Log eerst even uit - check al je browser tabs!");
      return;
    }
    this.clearLocalStorage();
    this.setUser(loginAsUser);
    return this.isLoggedIn();
  }

  async autoLoginMetrics(token: string): Promise<boolean> {
    const loginAsUser = (await this.get<User>(`unauthenticated/user/autologin/metrics/${token}`)).result;
    if (this.getUser()?.id && this.getUser().id !== loginAsUser?.id) {
      this.messageService.showErrorMessage("Log eerst even uit - check al je browser tabs!");
      return;
    }
    this.clearLocalStorage();
    this.setUser(loginAsUser);
    return this.isLoggedIn();
  }

  async register(email: string, password: string, newsletter: boolean): Promise<boolean> {
    const user = this.getUser() || <User>{};
    this.clearLocalStorage();
    user.email = email;
    user.password = password;
    user.newsletter = newsletter;
    await this.completeUserBeforeSaving(user);
    this.setUser((await this.post<User>("unauthenticated/user/register", {user})).result, true);
    return this.isLoggedIn();
  }

  async registerTempUser(email?: string): Promise<boolean> {
    const language = this.translate.getCurrentLang();
    this.setUser((await this.post<User>("unauthenticated/user/registertemp", {language, email})).result);
    return this.isLoggedIn();
  }

  async newsletterSignUp(email: string): Promise<User> {
    return (await this.post<User>("unauthenticated/user/newsletter", {email})).result;
  }

  async resetPassword(user: User): Promise<boolean> {
    const result = (await this.post<User>("unauthenticated/user/resetpasswordforuser", {user}));
    if (result.success) {
      this.setUser(result.result);
    }
    return result.success;
  }

  async loadUserByAPIOAuthKeyAndUrlService(apiOAuthKey: string, url: string): Promise<CombiPackageLoadUserResult> {
    return (await this.get<CombiPackageLoadUserResult>("unauthenticated/loaduserbyapioauthkeyandurl", {apiOAuthKey, url})).result;
  }

  async loadUserByAPIOAuthKeyAndExternalApiIDService(apiOAuthKey: string, externalApiID: string): Promise<CombiPackageLoadUserResult> {
    return (await this.get<CombiPackageLoadUserResult>("unauthenticated/loaduserbyapioauthkeyandexternalapiid", {apiOAuthKey, externalApiID})).result;
  }

  async changeUser(changeUser: User): Promise<User> {
    const updatedUser = await this.post<User>("authenticated/changeuser/save", {user: this.user, changeUser});
    if (updatedUser) {
      this.setUser(updatedUser.result);
    }
    return this.user;
  }

  async refreshUser(): Promise<User> {
    this.setUser((await this.post<User>("authenticated/loaduser", {user: this.user})).result);
    return this.user;
  }

  async changePassword(changePassword: ChangePassword): Promise<User> {
    this.setUser((await this.post<User>("authenticated/changepassword/save", {user: this.user, changePassword})).result);
    return this.user;
  }

  async confirmRegistration(token: string): Promise<void> {
    this.setUser(<User>(await this.get("unauthenticated/user/register/confirm/" + token)).result);
  }

  async getAnonymousIntercomJwt(): Promise<User> {
    return <User>(await this.get("unauthenticated/user/anonymous/intercom/jwt")).result;
  }

  async deleteAccount() {
    shutdownIntercom();
    if ((await this.post("authenticated/user/delete")).success) {
      this.logout();
    }
  }

  async logout() {
    await this.endSession();
    // don't navigate to '/', because when already there, the page won't reload and you don't 'see' you're logged out
    this.router.navigate(["/home"]);
  }

  /**
   * Ends the current session without navigating, for callers that decide where to go next themselves (e.g. route guards).
   */
  async endSession() {
    if (this.user?.cindyLoper) {
      // logout from backend in case we're a superuser, so userapi logging can be disabled again
      await this.post("unauthenticated/user/logout", {user: this.user}); // no need to wait, but just in case..
    }
    this.setUser(undefined);
    this.clearLocalStorage();
  }

  public clearLocalStorage(): void {
    this.localStorageService.remove(LocalStorageKey.USER);
    syncSessionCookie(false);
    this.localStorageService.remove(LocalStorageKey.CURRENT_USERAPI);
    this.localStorageService.remove(LocalStorageKey.OAUTHRESULT);
    this.localStorageService.remove(LocalStorageKey.CP_USERCOMBIPACKAGE);
    this.localStorageService.remove(LocalStorageKey.CP_COMBIPACKAGE);
    this.localStorageService.remove(LocalStorageKey.CP_EMAIL);
    this.localStorageService.remove(LocalStorageKey.CP_PACKAGEPARAMS);
    this.localStorageService.remove(LocalStorageKey.IS_EDIT);
  }

  private async completeUserBeforeSaving(user: User) {
    if (!user.language) {
      user.language = this.translate.getCurrentLang();
    }
    if (environment.env !== "dev") {
      if (!user.countryCode || !user.city) {
        const res: { country_code: string; city: string } = await firstValueFrom(this.http.get<any>("https://api.ipdata.co/?api-key=8873beb43826ee470c05e6fe055df64b028868a3a71ae05590881134"));
        user.countryCode = res?.country_code;
        user.city = res?.city;
      }
    }
  }
}
